import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Gauge, Coins, MessageSquare, Zap, Loader2, TrendingUp, Info,
} from "lucide-react";

const PERIODS = [
  { d: 7, label: "7 giorni" },
  { d: 30, label: "30 giorni" },
  { d: 90, label: "90 giorni" },
];

const fmt = (n) => new Intl.NumberFormat("it-IT").format(Math.round(n || 0));
const fmtUsd = (n) => `$${(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const KpiCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-md p-5 flex flex-col gap-2">
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
      <Icon className={`h-4 w-4 ${accent}`} />{label}
    </div>
    <div className="text-3xl font-extrabold tracking-tighter">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function ConsumiAI() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (user && user.role !== "super_admin") navigate("/", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/usage/summary?days=${days}`);
      setData(r.data);
      setTracking(r.data.tracking_enabled);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento consumi");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const toggleTracking = async (val) => {
    setToggling(true);
    setTracking(val);
    try {
      await api.put("/usage/settings", { enabled: val });
      toast.success(val ? "Monitoraggio consumi attivato" : "Monitoraggio consumi disattivato");
    } catch (e) {
      setTracking(!val);
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    } finally { setToggling(false); }
  };

  const t = data?.totals || {};
  const maxFn = Math.max(1, ...(data?.by_fn || []).map((x) => x.total_tokens));
  const chartData = (data?.by_day || []).map((x) => ({
    day: x.day?.slice(5) || "", token: x.total_tokens, cost: x.est_cost_usd,
  }));

  return (
    <div className="fade-in">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Monitoraggio Costi AI</div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-head text-4xl font-extrabold tracking-tighter">Consumi AI</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">Token consumati e costo stimato delle richieste all'intelligenza artificiale (Universal Key), suddivisi per funzione e per giorno.</p>
          </div>
          <div className="flex items-center gap-3 bg-card border border-border rounded-md px-4 py-2.5" data-testid="tracking-toggle-wrap">
            <div className="text-sm">
              <div className="font-medium">Monitoraggio</div>
              <div className="text-xs text-muted-foreground">{tracking ? "Attivo" : "Disattivato"}</div>
            </div>
            <Switch data-testid="tracking-toggle" checked={tracking} disabled={toggling} onCheckedChange={toggleTracking} />
          </div>
        </div>
      </header>

      <div className="p-4 sm:p-10 space-y-8">
        <div className="flex items-center gap-2" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button key={p.d} data-testid={`period-${p.d}`} onClick={() => setDays(p.d)}
              className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors duration-150 ${days === p.d ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border hover:bg-secondary"}`}>
              {p.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard testid="kpi-calls" icon={MessageSquare} accent="text-[#007BFF]" label="Chiamate AI" value={fmt(t.calls)} sub={`negli ultimi ${data.days} giorni`} />
              <KpiCard testid="kpi-tokens" icon={Zap} accent="text-amber-500" label="Token totali" value={fmt(t.total_tokens)} sub={`${fmt(t.prompt_tokens)} input · ${fmt(t.completion_tokens)} output`} />
              <KpiCard testid="kpi-cost" icon={Coins} accent="text-emerald-500" label="Costo stimato" value={fmtUsd(t.est_cost_usd)} sub="stima indicativa" />
              <KpiCard testid="kpi-avg" icon={Gauge} accent="text-violet-500" label="Media / chiamata" value={fmt(t.calls ? t.total_tokens / t.calls : 0)} sub="token per richiesta" />
            </div>

            <div className="bg-card border border-border rounded-md p-6">
              <div className="flex items-center gap-2 mb-5"><TrendingUp className="h-5 w-5 text-[#007BFF]" /><h3 className="font-head text-lg font-semibold">Token per giorno</h3></div>
              {chartData.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm">Nessun consumo registrato nel periodo.</div>
              ) : (
                <div style={{ width: "100%", height: 260 }} data-testid="usage-chart">
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <Tooltip formatter={(v, n) => n === "token" ? [fmt(v), "Token"] : [v, n]} labelStyle={{ color: "#111" }} contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
                      <Bar dataKey="token" fill="#007BFF" radius={[4, 4, 0, 0]} maxBarSize={38} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-md p-6">
                <h3 className="font-head text-lg font-semibold mb-4">Consumo per funzione</h3>
                {(data.by_fn || []).length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">Nessun dato.</div>
                ) : (
                  <div className="space-y-3" data-testid="by-fn-list">
                    {data.by_fn.map((f, i) => (
                      <div key={i} data-testid={`fn-row-${i}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium">{f.fn}</span>
                          <span className="text-muted-foreground text-xs">{fmt(f.total_tokens)} tok · {fmtUsd(f.est_cost_usd)} · {f.calls} ch.</span>
                        </div>
                        <div className="h-2 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full bg-[#007BFF] rounded-full transition-all duration-500" style={{ width: `${(f.total_tokens / maxFn) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-card border border-border rounded-md p-6">
                <h3 className="font-head text-lg font-semibold mb-4">Ultime richieste</h3>
                {(data.recent || []).length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">Nessuna richiesta recente.</div>
                ) : (
                  <div className="divide-y divide-border -my-1" data-testid="recent-list">
                    {data.recent.map((r, i) => (
                      <div key={r.id || i} data-testid={`recent-row-${i}`} className="flex items-center justify-between py-2.5 text-sm">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{r.fn}</div>
                          <div className="text-xs text-muted-foreground">{fmtTime(r.created_at)} · {r.latency_ms} ms</div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className="font-semibold text-[#007BFF]">{fmt(r.total_tokens)} tok</div>
                          <div className="text-[11px] text-muted-foreground">{fmtUsd(r.est_cost_usd)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Il costo è una <b>stima indicativa</b> calcolata sui token (input {fmtUsd(0.15)}/1M, output {fmtUsd(0.60)}/1M). I token sono stimati lato applicazione; i valori effettivi fatturati sulla Universal Key possono variare in base al modello utilizzato.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
